import type { PropsWithChildren } from 'react';
import { useQuery } from '@tanstack/react-query';
import { WarningCircle } from '@phosphor-icons/react';
import { dictionary } from '../i18n';
import { api, ApiError } from '../lib/api';
import { getTmaUser, isTmaEnv } from '../lib/tma';
import { Logo } from './shell/Logo';
import { Spinner } from './ui';

/** A gate screen before the app (spec 6.3): centred, the product mark on top, no menu. */
function Gate({ children }: PropsWithChildren) {
  return (
    <div className="min-h-screen flex items-center justify-center p-6 text-center safe-top safe-bottom">
      <div className="w-full max-w-sm flex flex-col items-center">
        <Logo className="mb-6" />
        {children}
      </div>
    </div>
  );
}

/**
 * Loads the profile once and keeps it in the ['profile'] cache. Every screen
 * below reads the same cache, without another request.
 */
export function RequireAuth({ children }: PropsWithChildren) {
  const inTma = isTmaEnv();
  // The profile is not loaded yet: take the language from Telegram itself.
  const t = dictionary(getTmaUser()?.language_code);
  const { data, isLoading, error } = useQuery({
    queryKey: ['profile'],
    queryFn: () => api.getProfile(),
    enabled: inTma,
    staleTime: Infinity,
  });

  if (!inTma) {
    return (
      <Gate>
        <h1 className="text-xl font-extrabold mb-2">{t.auth.outsideTitle}</h1>
        <p className="text-sm text-hint">{t.auth.outsideText}</p>
      </Gate>
    );
  }

  if (error) {
    const msg = error instanceof ApiError ? `${error.code}: ${error.message}` : String(error);
    return (
      <Gate>
        <div className="w-14 h-14 rounded-2xl bg-destructive-subtle text-destructive flex items-center justify-center mb-3">
          <WarningCircle size={28} />
        </div>
        <h1 className="text-lg font-bold mb-2">{t.auth.errorTitle}</h1>
        <p className="text-sm text-hint break-words">{msg}</p>
      </Gate>
    );
  }

  if (isLoading || !data) {
    return (
      <Gate>
        <Spinner />
        <span className="text-sm text-hint mt-3">{t.auth.loadingProfile}</span>
      </Gate>
    );
  }

  return <>{children}</>;
}
